import { FONT_MONO, FONT_SANS, hexPoints, v } from '../constants';
import type { Difficulty } from '../types';

// ─────────────────────────────────────────────────────────────────────
// UI atoms
// ─────────────────────────────────────────────────────────────────────
export function Wordmark({ large = false }: { large?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg
        width={large ? 28 : 22}
        height={large ? 28 : 22}
        viewBox="-12 -12 24 24"
        style={{ flex: 'none' }}
        aria-hidden="true"
      >
        <polygon
          points={hexPoints(0, 0, 10)}
          fill={v('ink')}
          stroke={v('ink')}
          strokeWidth={1}
          strokeLinejoin="round"
        />
        {/* x with a superscript ², echoing "exponents" in the wordmark */}
        <text
          x={-2.4} y={1}
          textAnchor="middle" dominantBaseline="central"
          fontFamily={FONT_MONO} fontSize={10} fontWeight={500}
          fill={v('paper')}
        >x</text>
        <text
          x={3.2} y={-2.5}
          textAnchor="middle" dominantBaseline="central"
          fontFamily={FONT_MONO} fontSize={7} fontWeight={500}
          fill={v('paper')}
        >2</text>
      </svg>
      <span style={{
        fontFamily: FONT_SANS,
        fontWeight: 600,
        fontSize: large ? 22 : 17,
        color: v('ink'),
        letterSpacing: '-0.02em',
      }}>Hexponents</span>
    </div>
  );
}

// Premium difficulties — Expert today is technically premium-to-PLAY,
// but free users can still SELECT it to see a blurred preview as an
// upgrade tease (the actual play-prevention happens in App.tsx via a
// blur + overlay on the board area). The ★ stays as a visual marker
// so the tier is recognizable.
export const PREMIUM_DIFFICULTIES = new Set<Difficulty>(['Expert']);

export function DifficultyTabs({ value, onChange }: { value: Difficulty; onChange: (d: Difficulty) => void }) {
  return (
    <div className="hm-diff" role="tablist" aria-label="Difficulty">
      {(['Easy', 'Medium', 'Hard', 'Expert'] as Difficulty[]).map(d => {
        return (
          <button
            key={d}
            role="tab"
            aria-selected={value === d}
            className={value === d ? 'active' : ''}
            onClick={() => onChange(d)}
            title={PREMIUM_DIFFICULTIES.has(d) ? 'Premium — Upgrade to play' : undefined}
          >
            {d}
            {PREMIUM_DIFFICULTIES.has(d) && <span style={{ marginLeft: 3, fontSize: 9, opacity: 0.65 }}>★</span>}
          </button>
        );
      })}
    </div>
  );
}

export function MarkLegend() {
  const items: [string, string][] = [
    ['Σ',    'Sum equals'],
    ['×',    'Product equals'],
    ['≥',    'Sum at least'],
    ['Even', 'All even'],
    ['Pos.',     'No negatives'],
    ['All Neg.', 'All values negative'],
    ['N Neg.',   'Exactly N negatives'],
  ];
  return (
    <div className="hm-marks">
      {items.map(([m, l]) => (
        <div className="hm-mark" key={m}>
          <div className="hm-mark-tag">{m}</div>
          <div className="hm-mark-label">{l}</div>
        </div>
      ))}
    </div>
  );
}
